import { useState, useEffect } from 'react';

import { BREAKPOINTS } from '@repo/ui/queries';

type WindowSize = {
  width: number | undefined;
  height: number | undefined;
  isMobile: boolean | undefined;
  isTabletMin: boolean | undefined;
};

type UseWindowSizeOptions = {
  shouldInitialize?: boolean;
};

/**
 * Determines if the current device is a mobile device based on the window's
 * width.
 *
 * A device is considered mobile if the window's width is smaller than the
 * `largeTabletMin` breakpoint defined in the `BREAKPOINTS` object.
 *
 * @returns If the current window size is meeting the mobile dimensions.
 */
export function isMobile(): boolean {
  return window.innerWidth < BREAKPOINTS.largeTabletMin;
}

/**
 * Determines if the current device meets the minimum width for tablets based on
 * the window's width.
 *
 * A device meets the tablet minimum if the window's width is smaller than the
 * `tabletMin` breakpoint defined in the `BREAKPOINTS` object.
 *
 * @returns  If the current window size is meeting the minimal tablet dimensions.
 */
export function isTabletMin(): boolean {
  return window.innerWidth < BREAKPOINTS.tabletMin;
}

// Initialize state with undefined width/height so server and client renders match for Server Side Rendering
// Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
const createInitialState = (shouldInitialize: boolean): WindowSize => ({
  width:
    shouldInitialize && window !== undefined ? window.innerWidth : undefined,
  height:
    shouldInitialize && window !== undefined ? window.innerHeight : undefined,
  isMobile: shouldInitialize && window !== undefined ? isMobile() : undefined,
  isTabletMin:
    shouldInitialize && window !== undefined ? isTabletMin() : undefined,
});

/**
 * A hook to get the current window size.
 *
 * @param shouldInitialize - Whether to initialize the state. Sometimes we need to skip the rendering with "undefined" values in Client Side Applications.
 * @returns The current window size.
 */
const useWindowSize = ({
  shouldInitialize = false,
}: UseWindowSizeOptions = {}): WindowSize => {
  const [windowSize, setWindowSize] = useState<WindowSize>(() =>
    createInitialState(shouldInitialize)
  );

  useEffect(() => {
    // Handler to call on window resize
    const handleResize = (): void => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isMobile: isMobile(),
        isTabletMin: isTabletMin(),
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return (): void => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

export default useWindowSize;
